import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { componentTypeLabel } from '@netfoundry-ui/feature/form/latency-policy-form';
import {
    AlertServiceV2,
    AlertTypeRegistry,
    LatencyComponentService,
    PickableComponent,
    ValidationErrorIndex,
} from '@netfoundry-ui/shared/apiv2';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import {
    ComponentType,
    OfflinePolicy,
    OfflinePolicyRequest,
    OfflinePolicyUpdate,
    OfflineRole,
    OfflineRungs,
} from '@netfoundry-ui/shared/model';
import { ApiService, LoggerService, URLS } from '@netfoundry-ui/shared/services';
import { isEmpty, isNil } from 'lodash';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import {
    OFFLINE_ROLES,
    OFFLINE_RUNGS,
    inheritedLadder,
    offlineComponentIds,
    offlineKindLabel,
    offlineTypeDetail,
    rungPlaceholders,
} from './offline-policy-text';

const CREATE_SEGMENT = 'create';
const SEARCH_MIN_LENGTH = 2;
const SEARCH_DELAY_MS = 300;
const EXAMPLE_COMPONENT_ID = '7ML1E8N5Sx438PQUOBAR9f';
/* Over the console's own overlays, which the picker panel is appended next to. */
const PICKER_BASE_Z_INDEX = 99999;

const COMPONENT_PATHS = ['$.componentType', '$.role', '$.componentIds', '$.componentIds[0]'];
const LADDER_PATHS = ['$.ladder', ...OFFLINE_RUNGS.map((rung) => `$.ladder.${rung.key}`)];
const FIELD_PATHS = [...COMPONENT_PATHS, '$.enabled', ...LADDER_PATHS];

/**
 * Creates an offline policy for one component, or edits any offline policy. Network defaults are created on the
 * listing; this page edits them when opened from there, with the scope locked.
 */
@Component({
    selector: 'app-offline-policy-form',
    templateUrl: './offline-policy-form.component.html',
    styleUrls: ['./offline-policy-form.component.scss'],
    standalone: false,
})
export class OfflinePolicyFormComponent implements OnInit, OnDestroy {
    readonly componentTypeLabel = componentTypeLabel;
    readonly offlineTypeDetail = offlineTypeDetail;
    readonly searchMinLength = SEARCH_MIN_LENGTH;
    readonly searchDelay = SEARCH_DELAY_MS;
    readonly pickerOverlay = { baseZIndex: PICKER_BASE_Z_INDEX };

    policy?: OfflinePolicy;
    entityId = CREATE_SEGMENT;

    componentType?: ComponentType;
    role?: OfflineRole;
    componentId = '';
    componentPick: PickableComponent | string | null = null;
    componentOptions: PickableComponent[] = [];
    componentSuggestions: PickableComponent[] = [];

    enabled = true;
    ladder: OfflineRungs = {};
    ladderInvalid = false;

    isLoading = false;
    processing = false;
    showErrors = false;
    saveError = '';
    /** What the service said about a policy it stored anyway. The form stays open to show it. */
    savedWarnings: string[] = [];
    serverErrors = new ValidationErrorIndex();
    currentNetwork: any = {};

    private networkPolicy?: OfflinePolicy;
    private componentNames = new Map<string, string>();
    private subscription = new Subscription();
    private pendingQuery = '';
    private touched = new Set<string>();
    private lastCheckedBody = '';
    private checkSequence = 0;
    /** True while {@link saveError} holds a 403 from a check, so a later accepted check can clear it. */
    private checkDenied = false;

    constructor(
        private alertService: AlertServiceV2,
        private alertTypes: AlertTypeRegistry,
        private components: LatencyComponentService,
        private apiService: ApiService,
        private growlerService: GrowlerService,
        private logger: LoggerService,
        private router: Router,
        private route: ActivatedRoute
    ) {}

    get isEditing(): boolean {
        return this.entityId !== CREATE_SEGMENT;
    }

    get isNetworkDefault(): boolean {
        return !!this.policy && offlineComponentIds(this.policy).length === 0;
    }

    get title(): string {
        return this.isEditing ? 'Edit Offline Policy' : 'Create Offline Policy';
    }

    get hasNetwork(): boolean {
        return !isEmpty(this.currentNetwork.id);
    }

    get componentTypeOptions(): ComponentType[] {
        return this.alertTypes.offlineComponentTypes;
    }

    get componentLabel(): string {
        return componentTypeLabel(this.componentType) || 'Component';
    }

    /** The roles a policy for the chosen type must name one of. Empty for a type graded on one ladder. */
    get roleOptions(): { value: OfflineRole; label: string; detail: string }[] {
        const roles = this.alertTypes.offlineRoles(this.componentType);
        return OFFLINE_ROLES.filter((option) => roles.includes(option.value));
    }

    get needsRole(): boolean {
        return this.roleOptions.length > 0;
    }

    /** Whether the rule runs for the chosen kind where no policy says. */
    get enabledByDefault(): boolean {
        return this.alertTypes.offlineEnabledByDefault(this.componentType, this.role);
    }

    /** A ladder left empty takes the next level's, except on a network default of a kind that is on anyway. */
    get ladderMayBeEmpty(): boolean {
        return !this.isNetworkDefault || !this.enabledByDefault;
    }

    get saved(): boolean {
        return this.savedWarnings.length > 0;
    }

    get canPick(): boolean {
        return this.componentOptions.length > 0;
    }

    get componentPlaceholder(): string {
        return this.canPick ? `Search ${this.componentLabel.toLowerCase()}` : `e.g. ${EXAMPLE_COMPONENT_ID}`;
    }

    /** The ladder an empty box falls back to: the network default for an override, else the fleet ladder. */
    get inherited(): OfflineRungs | undefined {
        const fleet = this.alertTypes.offlineFleetDefaults(this.componentType, this.role);
        return this.isNetworkDefault ? inheritedLadder(fleet) : inheritedLadder(this.networkPolicy?.ladder, fleet);
    }

    get placeholders(): Record<keyof OfflineRungs, string> {
        return rungPlaceholders(this.inherited);
    }

    get inheritedFrom(): string {
        return this.isNetworkDefault || !this.networkPolicy?.ladder ? 'the platform default' : 'the network policy';
    }

    get lockedScope(): string {
        const kind = offlineKindLabel(this.componentType, this.role) || this.componentLabel;
        if (this.isNetworkDefault) {
            return `Network policy — every ${kind.toLowerCase()} in this network`;
        }
        const id = offlineComponentIds(this.policy)[0] || '';
        const name = this.componentNames.get(id);
        return name ? `${kind} · ${name} (${id})` : `${kind} · ${id}`;
    }

    /** The alerting choices wait for a component and a role on a create, because the service checks them against that key. */
    get canEditLadder(): boolean {
        if (this.saved) {
            return false;
        }
        return this.isEditing || (!isEmpty(this.componentId.trim()) && (!this.needsRole || !isNil(this.role)));
    }

    get ladderLockedReason(): string {
        if (this.canEditLadder || this.saved) {
            return '';
        }
        return this.needsRole && isNil(this.role)
            ? `Choose the ${this.componentLabel.toLowerCase()} and its role first.`
            : `Choose the ${this.componentLabel.toLowerCase()} first.`;
    }

    get update(): OfflinePolicyUpdate {
        return { enabled: this.enabled, ladder: this.enabled ? this.ladder : null };
    }

    get request(): OfflinePolicyRequest {
        return {
            componentType: this.componentType as ComponentType,
            role: this.needsRole ? this.role : undefined,
            componentIds: isEmpty(this.componentId.trim()) ? [] : [this.componentId.trim()],
            ...this.update,
        };
    }

    get scopeError(): string {
        if (this.isEditing || !this.showErrors) {
            return '';
        }
        if (isNil(this.componentType)) {
            return 'No component type takes an offline policy, or the list could not be loaded.';
        }
        return isEmpty(this.componentId.trim()) ? `Choose the ${this.componentLabel.toLowerCase()} this policy is for.` : '';
    }

    /** The one message for the role, from this form or from the service. */
    get roleError(): string {
        if (!this.isEditing && this.showErrors && this.needsRole && isNil(this.role)) {
            return 'Choose the role this policy is for.';
        }
        return this.errorFor('$.role');
    }

    /** The one message for the component picker, from this form or from the service. */
    get componentError(): string {
        return (
            this.scopeError ||
            this.errorFor('$.componentIds') ||
            this.errorFor('$.componentIds[0]') ||
            this.errorFor('$.componentType')
        );
    }

    /** Whether any message is about the ladder, which turns its label red. */
    get hasLadderError(): boolean {
        return LADDER_PATHS.some((path) => !isEmpty(this.errorFor(path)));
    }

    get rungErrors(): Partial<Record<keyof OfflineRungs, string>> {
        const errors: Partial<Record<keyof OfflineRungs, string>> = {};
        for (const rung of OFFLINE_RUNGS) {
            errors[rung.key] = this.errorFor(`$.ladder.${rung.key}`);
        }
        return errors;
    }

    get unclaimedErrors(): string[] {
        return this.showErrors ? this.serverErrors.unclaimed(FIELD_PATHS) : [];
    }

    ngOnInit(): void {
        this.subscription.add(
            this.apiService.currentNetwork
                .pipe(distinctUntilChanged((before, after) => before?.id === after?.id))
                .subscribe((network) => {
                    this.currentNetwork = network || {};
                    this.alertTypes.load(this.currentNetwork.id).then(() => {
                        if (!this.isEditing && isNil(this.componentType)) {
                            this.setComponentType(this.componentTypeOptions[0]);
                        }
                    });
                })
        );
        this.subscription.add(
            this.route.params.subscribe((params) => {
                const id = params['id'];
                this.entityId = isEmpty(id) ? CREATE_SEGMENT : id;
                if (this.isEditing) {
                    this.loadPolicy(this.entityId);
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    setComponentType(componentType?: ComponentType): void {
        if (isNil(componentType) || this.isEditing) {
            return;
        }
        this.componentType = componentType;
        this.role = undefined;
        this.componentId = '';
        this.componentPick = null;
        this.componentSuggestions = [];
        this.pendingQuery = '';
        this.loadComponentOptions();
        this.loadNetworkPolicy();
    }

    setRole(role: OfflineRole): void {
        if (this.isEditing || this.role === role) {
            return;
        }
        this.role = role;
        this.loadNetworkPolicy();
        this.fieldBlurred('$.role');
    }

    searchComponents(query: string): void {
        const kind = this.componentType;
        if (isNil(kind)) {
            return;
        }
        const term = query || '';
        this.pendingQuery = term;
        this.components.search(this.currentNetwork.id, kind, term).then((options) => {
            if (this.pendingQuery !== term || this.componentType !== kind) {
                return;
            }
            this.componentSuggestions = [...options];
        });
    }

    takeComponentPick(value: PickableComponent | string | null): void {
        this.componentPick = value;
        this.componentId = isNil(value) ? '' : typeof value === 'string' ? value.trim() : value.id;
        this.fieldBlurred('$.componentIds', '$.componentIds[0]');
    }

    setEnabled(enabled: boolean): void {
        if (this.enabled === enabled || !this.canEditLadder) {
            return;
        }
        this.enabled = enabled;
        this.fieldBlurred('$.enabled', ...LADDER_PATHS);
    }

    setLadder(ladder: OfflineRungs): void {
        this.ladder = ladder;
    }

    /* An ordering error lands on the later rung, not on the box just left, so leaving any box answers for every rung. */
    rungBlurred(): void {
        this.fieldBlurred(...LADDER_PATHS);
    }

    fieldBlurred(...paths: string[]): void {
        this.touched = new Set(paths);
        this.checkWithService();
    }

    errorFor(path: string): string {
        if (!this.showErrors && !this.touched.has(path)) {
            return '';
        }
        return this.serverErrors.messageFor(path);
    }

    save(): void {
        this.showErrors = true;
        this.saveError = '';
        this.checkDenied = false;
        if (!isEmpty(this.scopeError) || !isEmpty(this.roleError) || this.ladderInvalid || this.saved) {
            return;
        }
        if (!this.hasNetwork) {
            this.saveError = 'Select a network before setting an offline policy.';
            return;
        }
        this.processing = true;
        this.isLoading = true;
        const saved = this.isEditing
            ? this.alertService.updateOfflinePolicy(this.entityId, this.update)
            : this.alertService.createOfflinePolicy(this.currentNetwork.id, this.request);
        saved
            .then((policy) => {
                if (!isEmpty(policy?.warnings)) {
                    this.savedWarnings = policy.warnings as string[];
                    return;
                }
                this.growlerService.show(
                    new GrowlerData(
                        'success',
                        this.isEditing ? 'Offline policy updated' : 'Offline policy created',
                        policy?.networkDefault
                            ? `It applies to every ${this.componentLabel.toLowerCase()} in this network.`
                            : `It overrides the network policy for this ${this.componentLabel.toLowerCase()}.`,
                        ''
                    )
                );
                this.close();
            })
            .catch((error) => this.handleSaveError(error))
            .finally(() => {
                this.processing = false;
                this.isLoading = false;
            });
    }

    close(): void {
        this.router.navigateByUrl(URLS.OFFLINE_POLICIES);
    }

    /** Asks the service about the body Save would send. Skipped on a create until a component is chosen. */
    private checkWithService(): void {
        if (!this.hasNetwork || this.ladderInvalid) {
            return;
        }
        if (!this.isEditing && (isNil(this.componentType) || isEmpty(this.componentId.trim()) || (this.needsRole && isNil(this.role)))) {
            this.serverErrors = new ValidationErrorIndex();
            this.lastCheckedBody = '';
            this.checkSequence++;
            return;
        }
        const body = JSON.stringify(this.isEditing ? this.update : this.request);
        if (body === this.lastCheckedBody) {
            return;
        }
        this.lastCheckedBody = body;
        const sequence = ++this.checkSequence;
        const checked = this.isEditing
            ? this.alertService.validateOfflinePolicyUpdate(this.entityId, this.update)
            : this.alertService.validateOfflinePolicy(this.currentNetwork.id, this.request);
        checked
            .then((errors) => {
                if (sequence !== this.checkSequence) {
                    return;
                }
                this.serverErrors = new ValidationErrorIndex(errors);
                if (this.checkDenied) {
                    this.saveError = '';
                    this.checkDenied = false;
                }
            })
            .catch((error) => {
                this.lastCheckedBody = '';
                if (error?.status === 403) {
                    this.saveError = this.permissionMessage();
                    this.checkDenied = true;
                    return;
                }
                this.logger.error('The offline policy could not be checked', error);
            });
    }

    private loadPolicy(policyId: string): void {
        this.isLoading = true;
        this.alertService
            .getOfflinePolicy(policyId)
            .then((policy) => {
                this.policy = policy;
                this.componentType = policy.componentType;
                this.role = policy.role || undefined;
                this.componentId = offlineComponentIds(policy)[0] || '';
                this.enabled = policy.enabled;
                this.ladder = { ...(policy.ladder || {}) };
                this.loadNetworkPolicy();
                this.loadComponentOptions();
            })
            .catch((error) => {
                this.logger.error('Failed to read the offline policy', error);
                this.saveError = 'This policy could not be loaded. It may have been deleted.';
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    private loadNetworkPolicy(): void {
        this.networkPolicy = undefined;
        const kind = this.componentType;
        const role = this.role;
        if (isNil(kind) || !this.hasNetwork || this.isNetworkDefault || (this.needsRole && isNil(role))) {
            return;
        }
        this.alertService
            .listOfflinePoliciesPage(
                this.currentNetwork.id,
                { componentType: [kind], role: isNil(role) ? undefined : [role], networkDefault: true },
                1
            )
            .then((page) => {
                if (this.componentType === kind && this.role === role) {
                    this.networkPolicy = page.policies[0];
                }
            })
            .catch((error) => this.logger.error(`The network offline policy for ${kind} could not be read`, error));
    }

    private loadComponentOptions(): void {
        this.componentOptions = [];
        const kind = this.componentType;
        if (isNil(kind) || !this.hasNetwork) {
            return;
        }
        this.components.listFor(this.currentNetwork.id, kind).then((options) => {
            if (this.componentType !== kind) {
                return;
            }
            this.componentOptions = options;
            this.componentNames = new Map(options.map((option) => [option.id, option.name]));
            if (isEmpty(this.pendingQuery) && isEmpty(this.componentSuggestions)) {
                this.componentSuggestions = options;
            }
            this.nameLockedComponent(kind);
        });
    }

    /** The name of the component being edited, read by id when the one listed page does not hold it. */
    private nameLockedComponent(kind: ComponentType): void {
        const id = this.isEditing ? offlineComponentIds(this.policy)[0] : '';
        if (isEmpty(id) || this.componentNames.has(id)) {
            return;
        }
        this.components.namesFor(this.currentNetwork.id, kind, [id]).then((found) => {
            for (const option of found) {
                this.componentNames.set(option.id, option.name);
            }
        });
    }

    private handleSaveError(error: any): void {
        this.logger.error('Failed to save the offline policy', error);
        if (error?.status === 400) {
            this.lastCheckedBody = '';
            this.checkWithService();
            this.saveError = 'Some of these values were not accepted. The fields below say which.';
            return;
        }
        if (error?.status === 409) {
            this.saveError = `An offline policy already covers this ${this.componentLabel.toLowerCase()}. Open it from the listing and change it instead.`;
            return;
        }
        if (error?.status === 403) {
            this.saveError = this.permissionMessage();
            return;
        }
        if (error?.status === 503) {
            this.saveError = `The network's controller could not be reached to check this ${this.componentLabel.toLowerCase()}. Nothing was saved. Try again.`;
            return;
        }
        this.saveError = error?.error?.detail || 'The policy could not be saved.';
    }

    private permissionMessage(): string {
        return this.isEditing
            ? 'You do not have permission to change offline policies in this network.'
            : 'You do not have permission to create offline policies in this network.';
    }
}
